import Icon from "@hackclub/icons";
import type { ComponentProps, ReactNode } from "react";
import type { SparkBoxItem } from "@/lib/spark-box-manifest";

// The manifest's icon names are a literal port of the source app's data —
// several (e.g. "box", "globe") aren't in this icon set's typed glyph union
// and render blank there too. That's the real behavior of the source app,
// not a bug introduced here, so the cast preserves it rather than "fixing"
// it into icons the source page doesn't actually show.
type Glyph = ComponentProps<typeof Icon>["glyph"];

const colorMap: Record<string, { accent: string; tint: string }> = {
  primary: { accent: "var(--sbx-ember-deep)", tint: "var(--sbx-ember-tint)" },
  secondary: { accent: "var(--sbx-peri)", tint: "var(--sbx-peri-tint)" },
  accent: { accent: "var(--sbx-moss)", tint: "var(--sbx-moss-tint)" },
};

export function SparkBoxCard({
  item,
  footerAction,
}: {
  item: SparkBoxItem;
  /** Replaces "Try this →" in the footer (e.g. Delete on Curated Sparks). The
   *  card is then a plain panel rather than a button, since buttons can't nest. */
  footerAction?: ReactNode;
}) {
  const colors = colorMap[item.color] || colorMap.primary;

  const body = (
    <>
      <div className="sbx-card__top">
        <div className="sbx-card__icon" style={{ background: colors.tint, color: colors.accent }}>
          {item.icon && <Icon glyph={item.icon as Glyph} size={20} />}
        </div>
        {item.title && (
          <span className="sbx-card__eyebrow" style={{ color: colors.accent }}>
            {item.title}
          </span>
        )}
      </div>

      <h3 className="sbx-card__heading">{item.name}</h3>

      <p className="sbx-card__desc">{item.description}</p>
    </>
  );

  if (footerAction) {
    return (
      <div className="sbx-card sbx-card--static">
        {body}
        <div className="sbx-card__footer">
          {item.category && (
            <span
              className="sbx-card__category"
              style={{ background: colors.tint, color: colors.accent }}
            >
              {item.category}
            </span>
          )}
          {footerAction}
        </div>
      </div>
    );
  }

  const handleClick = () => {
    if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      type="button"
      className="sbx-card"
      onClick={handleClick}
      style={{ cursor: item.url ? "pointer" : "default" }}
    >
      <div className="sbx-card__top">
        <div className="sbx-card__icon" style={{ background: colors.tint, color: colors.accent }}>
          {item.icon && <Icon glyph={item.icon as Glyph} size={20} />}
        </div>
        {item.title && (
          <span className="sbx-card__eyebrow" style={{ color: colors.accent }}>
            {item.title}
          </span>
        )}
      </div>

      <h3 className="sbx-card__heading">{item.name}</h3>

      <p className="sbx-card__desc">{item.description}</p>

      <div className="sbx-card__footer">
        <span
          className="sbx-card__category"
          style={{ background: colors.tint, color: colors.accent }}
        >
          {item.category}
        </span>
        <span className="sbx-card__try">
          Try this <span>→</span>
        </span>
      </div>
    </button>
  );
}
